// Staff dashboard: my next shift, today's briefing, my tasks, my training and the weather.
// Staff only ever see their own shift times here.
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { CalendarClock, CheckSquare, GraduationCap, Cloud, ChevronRight, Circle } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import { useTasks } from '@/src/hooks/useTasks';
import { useTraining } from '@/src/hooks/useTraining';
import { useWeather } from '@/src/hooks/useWeather';
import { useVenueSetupFields } from '@/src/hooks/useVenueSetupFields';
import BriefingCard from './briefing-card';

type Shift = { id: string; start_time: string; end_time: string; role_tag: string | null };
const PASS = 70;
const go = (page: string) => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page } }));

function useMyShifts(venueId: string, userId: string) {
  const [shifts, setShifts] = useState<Shift[] | null>(null);
  useEffect(() => {
    let off = false;
    void (async () => {
      const since = new Date(Date.now() - 12 * 3_600_000).toISOString();
      const { data } = await supabase.from('shifts').select('id, start_time, end_time, role_tag, status')
        .eq('venue_id', venueId).eq('user_id', userId).eq('published', true).gte('end_time', new Date().toISOString()).gte('start_time', since)
        .order('start_time').limit(4);
      if (!off) setShifts(((data ?? []) as Array<Shift & { status?: string | null }>).filter((s) => s.status !== 'cancelled'));
    })();
    return () => { off = true; };
  }, [venueId, userId]);
  return shifts;
}

export default function StaffDashboard({ venueId, userId, userName, timezone }: { venueId: string; userId: string; userName?: string | null; timezone?: string | null }) {
  const c = useAppColors();
  const tz = timezone || 'Australia/Sydney';
  const first = (userName ?? '').split(' ')[0];
  const fullDate = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz });
  const card = { background: c.bgElevated, border: `1px solid ${c.border}` };

  const shifts = useMyShifts(venueId, userId);
  const { tasks, toggleDone } = useTasks(venueId, { mineOnly: true, userId });
  const myOpen = tasks.filter((t) => t.status !== 'done')
    .sort((a, b) => (a.due_at ?? '9999').localeCompare(b.due_at ?? '9999'));
  const { courses, assignments } = useTraining(venueId, userId);
  const mine = assignments.filter((a) => courses.some((x) => x.id === a.course_id));
  const done = mine.filter((a) => a.completed_at);
  const passed = done.filter((a) => (a.score ?? 0) >= PASS);
  const todo = mine.filter((a) => !a.completed_at);
  const recent = [...done].sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? '')).slice(0, 3);
  const { fields } = useVenueSetupFields(venueId);
  const weather = useWeather((fields?.latitude as number | null) ?? null, (fields?.longitude as number | null) ?? null);
  const [busyTask, setBusyTask] = useState<string | null>(null);

  const dayLabel = (iso: string) => {
    const d = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    const day = d.format(new Date(iso)), today = d.format(new Date()), tomorrow = d.format(new Date(Date.now() + 86_400_000));
    if (day === today) return 'Today';
    if (day === tomorrow) return 'Tomorrow';
    return new Date(iso).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', timeZone: tz });
  };
  const t = (iso: string) => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: tz }).replace(/\s/g, '').toLowerCase();
  const due = (iso: string | null) => {
    if (!iso) return null;
    const overdue = new Date(iso).getTime() < Date.now();
    return { text: overdue ? 'Overdue' : `${dayLabel(iso)} ${t(iso)}`, overdue };
  };
  const nameOf = (courseId: string) => courses.find((x) => x.id === courseId)?.title ?? 'Quiz';
  const next = shifts?.[0];
  const onNow = next && new Date(next.start_time).getTime() <= Date.now();

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
      className="px-4 sm:px-8 py-8 max-w-3xl mx-auto w-full space-y-4 pb-24">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" style={{ color: c.text }}>{first ? `Hi ${first}` : 'Dashboard'}</h1>
        <p className="text-sm mt-1" style={{ color: c.textDim }}>{fullDate}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Next shift */}
        <section className="rounded-2xl p-4 sm:col-span-2" style={card}>
          <p className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: c.textFaint }}><CalendarClock size={13} /> {onNow ? 'On now' : 'Next shift'}</p>
          {shifts === null ? <p className="text-sm mt-2" style={{ color: c.textDim }}>…</p>
            : !next ? <p className="text-sm mt-2" style={{ color: c.textDim }}>No upcoming shifts on the roster.</p>
            : (
              <>
                <p className="text-xl font-semibold mt-1.5" style={{ color: c.text }}>{dayLabel(next.start_time)} · {t(next.start_time)}–{t(next.end_time)}</p>
                {next.role_tag && <p className="text-sm" style={{ color: c.textDim }}>{next.role_tag}</p>}
                {shifts.length > 1 && (
                  <p className="text-xs mt-2" style={{ color: c.textFaint }}>Then {shifts.slice(1).map((s) => `${dayLabel(s.start_time)} ${t(s.start_time)}`).join(' · ')}</p>
                )}
              </>
            )}
        </section>
        {/* Weather */}
        <section className="rounded-2xl p-4" style={card}>
          <p className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: c.textFaint }}><Cloud size={13} /> Weather</p>
          {weather ? (
            <>
              <p className="text-xl font-semibold mt-1.5" style={{ color: c.text }}>{weather.icon} {weather.temp}°C</p>
              <p className="text-sm" style={{ color: c.textDim }}>{weather.description}</p>
            </>
          ) : <p className="text-sm mt-2" style={{ color: c.textDim }}>{fields && fields.latitude == null ? 'Venue address not set' : '…'}</p>}
        </section>
      </div>

      <BriefingCard venueId={venueId} userId={userId} isManager={false} tz={tz} />

      {/* My tasks */}
      <section className="rounded-2xl p-5" style={card}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: c.text }}><CheckSquare size={16} style={{ color: c.accentSoft }} /> My tasks</h2>
          <button onClick={() => go('tasks')} className="text-xs font-semibold flex items-center" style={{ color: c.accentSoft }}>All tasks <ChevronRight size={14} /></button>
        </div>
        {myOpen.length === 0 ? <p className="text-sm" style={{ color: c.textDim }}>Nothing assigned to you right now.</p> : (
          <ul>
            {myOpen.slice(0, 6).map((task, i) => {
              const d = due(task.due_at);
              return (
                <li key={task.id} className="flex items-center gap-3 py-2.5" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                  <button aria-label={`Mark "${task.title}" done`} disabled={busyTask === task.id}
                    onClick={async () => { setBusyTask(task.id); await toggleDone(task, userId); setBusyTask(null); }}
                    className="shrink-0 disabled:opacity-40" style={{ color: c.textDim }}>
                    <Circle size={20} />
                  </button>
                  <span className="flex-1 min-w-0 text-sm truncate" style={{ color: c.text }}>{task.title}</span>
                  {d && <span className="text-xs shrink-0" style={{ color: d.overdue ? c.danger : c.textDim }}>{d.text}</span>}
                </li>
              );
            })}
            {myOpen.length > 6 && <li className="text-xs pt-2" style={{ color: c.textFaint }}>+{myOpen.length - 6} more</li>}
          </ul>
        )}
      </section>

      {/* Training */}
      <section className="rounded-2xl p-5" style={card}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: c.text }}><GraduationCap size={16} style={{ color: c.accentSoft }} /> Training</h2>
          <button onClick={() => go('training')} className="text-xs font-semibold flex items-center" style={{ color: c.accentSoft }}>Open <ChevronRight size={14} /></button>
        </div>
        {mine.length === 0 ? <p className="text-sm" style={{ color: c.textDim }}>Nothing assigned yet.</p> : (
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm"><span style={{ color: c.text }}>{passed.length} of {mine.length} passed</span><span style={{ color: c.textDim }}>{todo.length ? `${todo.length} to do` : 'All done'}</span></div>
              <div className="h-2 rounded-full overflow-hidden mt-1.5" style={{ background: c.iconBg }}>
                <div className="h-full rounded-full" style={{ width: `${Math.round((passed.length / mine.length) * 100)}%`, background: c.accent }} />
              </div>
            </div>
            {todo.length > 0 && (
              <button onClick={() => go('training')} className="w-full text-left rounded-xl px-3 py-2.5 text-sm flex items-center justify-between" style={{ background: c.iconBg, color: c.text }}>
                <span className="truncate">Next: {nameOf(todo[0].course_id)}</span><ChevronRight size={15} style={{ color: c.textDim }} />
              </button>
            )}
            {recent.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: c.textFaint }}>Recent scores</p>
                {recent.map((a) => (
                  <div key={a.id} className="flex justify-between text-sm py-1">
                    <span className="truncate" style={{ color: c.text }}>{nameOf(a.course_id)}</span>
                    <span className="font-semibold shrink-0 ml-2" style={{ color: (a.score ?? 0) >= PASS ? c.accentSoft : c.danger }}>{a.score ?? 0}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </motion.div>
  );
}
